import { ReactElement, ReactNode, useMemo, useState } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { getMemberCheckCategoryLabel, getMemberCheckShortLabel } from '@domainConstants'
import { get, maxBy, reject } from 'lodash-es'
import { Box, CircularProgress, Tooltip, useTheme } from '@mui/material'
import { MaterialSymbol } from '@ui2/icons'
import { EditableLabel } from '@ui/forms'
import { useTransmissionStore } from '@editorStores'
import { useGuidToElementIncludingRips } from '@editorHooks'
import { resolveLineMemberLengthFromGuidToElement } from '../resolveLineMemberLengthM'
import { DesignFormRootValues } from '../types'
import {
  buildMemberCheckSettingSupportsContext,
  getAllowedOverutilizationPercentForCheck,
  isUtilizationError,
  isUtilizationWarning,
  isVibrationError,
} from '../utils'
import SmartSuggestion from './SmartSuggestion'

const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const fmt = (value: number) => numberFormatter.format(value)

type BigEtaProps = {
  label: ReactNode
  value: number
  statusColor: string
  displayOverride?: string
  /** Short label of the governing check (M, N, Q, …), shown below the value. */
  sublabel?: string
  /** Full readable category of the governing check — tooltip on the sublabel. */
  sublabelTooltip?: string
}

const BigEta = ({
  label,
  value,
  statusColor,
  displayOverride,
  sublabel,
  sublabelTooltip,
}: BigEtaProps): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      minWidth: 72,
    }}
  >
    <Box component="span" sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1 }}>
      {label}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Box
        component="span"
        sx={{
          fontSize: 26,
          fontWeight: 500,
          color: statusColor,

          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.05,
          letterSpacing: '-0.01em',
        }}
      >
        {displayOverride ?? fmt(value)}
      </Box>
      {sublabel ? (
        <Tooltip title={sublabelTooltip ?? ''} placement="bottom" arrow disableInteractive>
          <Box component="span" sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
            {sublabel}
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  </Box>
)

export type ElementStatusHeaderProps = {
  elementId: string
  /** Model GUID for the selected member — used to resolve line length for the subtitle. */
  geometryGuid?: string
  subtitle: string
  exported: boolean
  onExportedChange: (next: boolean) => void
  calcChecksPending?: boolean
  disableForm?: boolean
  /** Grouped child rows inherit export state from the parent bundle representative. */
  isChildElement?: boolean
}

const lengthSubtitleFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const ElementStatusHeader = ({
  elementId,
  geometryGuid,
  subtitle,
  exported,
  onExportedChange,
  calcChecksPending = false,
  disableForm = false,
  isChildElement = false,
}: ElementStatusHeaderProps): ReactElement => {
  const theme = useTheme()
  const guidToElement = useGuidToElementIncludingRips()

  const subtitleWithLength = useMemo(() => {
    if (!geometryGuid) return subtitle
    const lengthM = resolveLineMemberLengthFromGuidToElement(geometryGuid, guidToElement)
    if (lengthM == null) return subtitle
    return `${subtitle} · ${lengthSubtitleFormatter.format(lengthM)}\u00a0m`
  }, [geometryGuid, subtitle, guidToElement])

  const checksFieldPath = 'elements.0.checksSelectedElement'
  const memberSettingsFieldPath = 'elements.0.memberCheckSettingsSelectedElement'
  const crossSectionFieldPath = 'elements.0.crossSection'
  const exportedFieldPath = 'elements.0.exported'

  const { dirtyFields, touchedFields } = useFormState<DesignFormRootValues>()

  const checks = useWatch({
    name: checksFieldPath,
  }) as CombinedPositionCheck[] | undefined
  const checkSpecificSettings = useWatch({
    name: `${memberSettingsFieldPath}.check_specific_settings`,
  }) as MemberCheckSpecificSetting[] | undefined
  const watchedElementGuid = useWatch({
    name: 'elements.0.elementGuid',
  }) as string | undefined
  const crossSection = useWatch({
    name: crossSectionFieldPath,
  }) as CrossSectionTimberOrCLTOrSteel | null | undefined
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  // Saved tolerances only apply while cross section / support situation still
  // match the reference data they were saved with.
  const settingValidationContext = useMemo(
    () => ({
      crossSection,
      supports: buildMemberCheckSettingSupportsContext(
        watchedElementGuid,
        verticalTransmissionGraph,
      ),
    }),
    [crossSection, verticalTransmissionGraph, watchedElementGuid],
  )

  const {
    eta,
    etaC,
    etaShort,
    etaShortTooltip,
    etaAllowedOverutilizationPercent,
    etaCAllowedOverutilizationPercent,
    etaIsVibrationError,
  } = useMemo(() => {
    const checksForGuid = checks ?? []
    const supportChecks = checksForGuid.filter(check =>
      ['SupportCompression', 'SteelSupportCompression'].includes(check.check_type),
    )
    const nonSupportChecks = reject(checksForGuid, check =>
      ['SupportCompression', 'SteelSupportCompression'].includes(check.check_type),
    )

    const maxSupportCheck = maxBy(supportChecks, check => check.max_utilization)
    const maxNonSupportCheck = maxBy(nonSupportChecks, check => check.max_utilization)
    const maxAnyCheck = maxBy(checksForGuid, check => check.max_utilization)
    const etaSourceCheck = maxNonSupportCheck ?? maxAnyCheck
    const etaRaw = etaSourceCheck?.max_utilization
    const etaCRaw = maxSupportCheck?.max_utilization

    return {
      eta: etaRaw ?? 0,
      etaC: etaCRaw ?? 0,
      etaShort: getMemberCheckShortLabel(etaSourceCheck?.check_type),
      etaShortTooltip: getMemberCheckCategoryLabel(etaSourceCheck?.check_type),
      etaAllowedOverutilizationPercent: getAllowedOverutilizationPercentForCheck(
        etaSourceCheck,
        checkSpecificSettings,
        settingValidationContext,
      ),
      etaCAllowedOverutilizationPercent: getAllowedOverutilizationPercentForCheck(
        maxSupportCheck,
        checkSpecificSettings,
        settingValidationContext,
      ),
      etaIsVibrationError: isVibrationError(
        etaSourceCheck?.check_type,
        etaSourceCheck?.max_utilization,
      ),
    }
  }, [checkSpecificSettings, checks, settingValidationContext])

  const etaOver = etaIsVibrationError || isUtilizationError(eta, etaAllowedOverutilizationPercent)
  const etaCOver = isUtilizationError(etaC, etaCAllowedOverutilizationPercent)
  const etaNear = !etaOver && isUtilizationWarning(eta, etaAllowedOverutilizationPercent)
  const etaCNear = !etaCOver && isUtilizationWarning(etaC, etaCAllowedOverutilizationPercent)
  const worstOver = etaOver || etaCOver
  const worstNear = !worstOver && (etaNear || etaCNear)

  const statusColor = worstOver
    ? theme.palette.warning.main
    : worstNear
    ? '#B28100'
    : theme.palette.success.main
  const statusBg = worstOver ? '#FFF5E8' : worstNear ? '#FFF8E1' : '#F4FAF4'

  const etaCStatusColor = etaCOver
    ? theme.palette.warning.main
    : etaCNear
    ? '#B28100'
    : theme.palette.success.main

  const [exportedAccepted, setExportedAccepted] = useState(false)

  const materialChanged =
    !!get(touchedFields, crossSectionFieldPath) &&
    !exported &&
    !get(dirtyFields, exportedFieldPath) &&
    !exportedAccepted

  const exportToggleDisabled = disableForm || isChildElement

  return (
    <Box
      sx={{
        flexShrink: 0,
        background: statusBg,
        borderBottom: `1px solid ${statusColor}33`,
        borderLeft: `3px solid ${statusColor}`,
        px: 1.75,
        pt: 1.25,
        pb: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.35,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            maxWidth: '100%',
            lineHeight: 1,
          }}
        >
          {geometryGuid ? (
            <Box
              sx={{
                minWidth: 0,
                '& .MuiStack-root': {
                  minWidth: 0,
                },
                '& .MuiTypography-root': {
                  color: theme.palette.text.primary,
                  fontFamily: 'Roboto',
                  lineHeight: 1.1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiIconButton-root': {
                  flexShrink: 0,
                },
              }}
            >
              <EditableLabel elementGuid={geometryGuid} label={elementId} />
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                fontSize: 20,
                fontWeight: 500,
                color: theme.palette.text.primary,
                fontFamily: 'Roboto',
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {elementId}
            </Box>
          )}
          {calcChecksPending ? (
            <CircularProgress
              size={14}
              thickness={5}
              sx={{ flexShrink: 0, color: theme.palette.text.secondary }}
              aria-label="Nachweise werden berechnet"
            />
          ) : null}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: 12,
            color: theme.palette.text.secondary,
            fontFamily: 'Roboto',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitleWithLength}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <Tooltip
          title={
            isChildElement
              ? 'Export nur über den Gruppenrepräsentanten möglich'
              : exported
              ? 'Exportiert — klicken zum Zurücksetzen'
              : 'Als exportiert markieren'
          }
          arrow
          disableInteractive
        >
          <SmartSuggestion
            text="Bauteil exportieren "
            onAccept={() => {
              onExportedChange(true)
              setExportedAccepted(true)
            }}
            onAbort={() => {
              setExportedAccepted(false)
            }}
            enabled={materialChanged && !isChildElement}
          >
            <Box
              component="button"
              type="button"
              disabled={exportToggleDisabled}
              onClick={() => {
                if (exportToggleDisabled) return
                onExportedChange(!exported)
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                flexShrink: 0,
                px: 0.75,
                py: 0.5,
                pr: 1.5,
                minHeight: 28,
                border: 0,
                bgcolor: 'rgba(0,0,0,0.06)',
                cursor: exportToggleDisabled ? 'not-allowed' : 'pointer',
                opacity: exportToggleDisabled ? 0.5 : 1,
                fontFamily: 'Roboto',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: exportToggleDisabled ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.1)',
                },
                '&:hover .export-status-icon': exportToggleDisabled
                  ? undefined
                  : {
                      color: exported ? theme.palette.success.dark : theme.palette.success.main,
                    },
              }}
            >
              <Box
                className="export-status-icon"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  color: exported ? theme.palette.success.main : 'rgba(0,0,0,0.28)',
                }}
              >
                <MaterialSymbol icon="check_circle" filled={exported} size={16} />
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  color: exported ? theme.palette.success.main : theme.palette.text.secondary,
                  fontWeight: 500,
                  lineHeight: 1,
                  pr: 0.25,
                }}
              >
                {exported ? 'exportiert' : 'nicht exportiert'}
              </Box>
            </Box>
          </SmartSuggestion>
        </Tooltip>
        <Box sx={{ display: 'inline-flex', alignItems: 'stretch', gap: 2, ml: 0.5 }}>
          <BigEta
            label="η"
            value={eta}
            statusColor={statusColor}
            displayOverride={etaIsVibrationError ? 'ERR' : undefined}
            sublabel={etaShort}
            sublabelTooltip={etaShortTooltip}
          />
          <BigEta
            label={
              <>
                η
                <Box component="sub" sx={{ fontSize: 9 }}>
                  c,90
                </Box>
              </>
            }
            value={etaC}
            statusColor={etaCStatusColor}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default ElementStatusHeader
